import { EnvironmentDetail, globalEventBus } from '@amzn/ihm-associate-tools-js-lib'
import { cordovaExecWithCallback } from '@services/CandyCaneService'
import { AssociateToolsBaseEventBusTopics } from '@utils/Constants/AssociateToolsBaseEventBusTopics'

import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { getUserInformation } from './UserInfoService'
import { DeviceRegistryServiceClient } from '@api/DeviceRegistryServiceClient'
import { DeviceRegistryConstants } from '@utils/Constants/DeviceRegistryConstants'
/**
 * DeviceRegistryService
 * This Service is responsible for sending pushToken and other metadata to backend DeviceRegistryServiceLambda.
 * DeviceRegistryServiceLambda stored pushToken and userAlias mapping in dataStore.
 * pushToken is used by backend to send push notifications on candycane using Google FCM.
 * Detail doc: https://quip-amazon.com/dNt9Ank1YBZw/Kaspian-Notifications
 *
 * pushToken: Acts as device endpoint to send push notification.
 * appInstallId: Unique id for app instance, generated at backend for first time and stored in candycane shared preferences.
 */

/**
 * Call actiavte to initialize Device Registry Service.
 * Comment Device Registry Service activate before enabling in Atbase.
 * Activates the listeners for seaward:login events.
 * Register pushToken refresh callback in candycane plugin.
 */
export const activate = () => {
    if (EnvironmentDetail.isAndroid()) {
        console.info('ATBase: Activating deviceRegistryService listeners.')
        globalEventBus.on(AssociateToolsBaseEventBusTopics.LOGIN, 'onLoginDeviceRegister', onLoginRegisterDevice)
        registerTokenRefreshCallback()
    }
}

/**
 * This method listens to the seaward:login event. When user log in, this method is triggered.
 * Collects appInstallId, pushToken, userAlias before making registerDevice call.
 * Sends appInstallId returned from registerDevice API to NotificationPlugin.
 */
export const onLoginRegisterDevice = async () => {
    console.info('ATBase: deviceRegistryService onLoginRegisterDevice')
    const deferred = generateDeferredPromise()
    if (EnvironmentDetail.isAndroid()) {
        try {
            const appInstallId = await getAppInstallId()
            const pushToken = await getPushToken()
            const userAlias = await getUserAlias()
            console.info('ATBase: DeviceRegistry onLoginRegisterDevice appInstallId: ',JSON.stringify(appInstallId))
            console.info('ATBase: DeviceRegistry onLoginRegisterDevice pushToken: ',pushToken)
            console.info('ATBase: DeviceRegistry onLoginRegisterDevice userAlias: ',userAlias)
            const response = await registerDevice(appInstallId, pushToken, userAlias)
            if (response.body?.appInstallId) {
                await setAppInstallId(response.body.appInstallId)
            }
        } catch (error) {
            console.error('ATBase: DeviceRegistry onLoginRegisterDevice device registry update failed. ', JSON.stringify(error))
        } finally {
            deferred.resolve({})
        }
    } else {
        console.debug('Not an android device. Logging out.')
        deferred.resolve({})
    }
    return deferred.promise
}

/**
 * Fetches appInstallId from NotificationPlugin.
 * @returns {Promise}
 */
const getAppInstallId = async () => {
    try {
        const response = await cordovaExecWithCallback(null, null, DeviceRegistryConstants.NOTIFICATION_PLUGIN, DeviceRegistryConstants.ACTION_GET_APP_INSTALL_ID, [])
        console.info('ATBase: DeviceRegistry: appInstalled received from notificationPlugin: ', JSON.stringify(response))
        if (response) {
            console.info('ATBase: DeviceRegistry: returning appInstalled')
            return Promise.resolve(response)
        }
        return Promise.resolve('')
    } catch (error) {
        console.error('ATBase: DeviceRegistry error occurred while fetching appInstallId from notificationPlugin. ', JSON.stringify(error))
        return Promise.resolve('')
    }
}

/**
 * Fetches pushToken from NotificationPlugin.
 * @returns {Promise}
 */
const getPushToken = async (): Promise<any> => {
    try {
        const response = await cordovaExecWithCallback(null, null, DeviceRegistryConstants.NOTIFICATION_PLUGIN, DeviceRegistryConstants.ACTION_GET_TOKEN, [])
        console.info('ATBase: DeviceRegistry pushToken received from notificationPlugin:')
        return Promise.resolve(response)
    } catch (error) {
        console.error('ATBase: DeviceRegistry error occurred while fetching pushToken from notificationPlugin: ',JSON.stringify(error))
        return Promise.reject(error)
    }
}

/**
 * Sends appInstallId to NotificationPlugin which persists it in local storage.
 * @param {string} appInstallId: appInstallId string.
 */
const setAppInstallId = async (appInstallId: string) => {
    try {
        console.info('ATBase: DeviceRegistry setAppInstallId appInstallId: ',JSON.stringify(appInstallId))
        await cordovaExecWithCallback(null, null, DeviceRegistryConstants.NOTIFICATION_PLUGIN, DeviceRegistryConstants.ACTION_SET_APP_INSTALL_ID, [{ appInstallId }])
        console.info('ATBase: DeviceRegistry setAppInstallId appInstallId successfully set to notificationPlugin.')
    } catch (error) {
        console.error('ATBase: DeviceRegistry setAppInstallId error occurred while persisting appInstallId to notificationPlugin: ', JSON.stringify(error))
    }
}

/**
 * Register callbackContext in NotificationPlugin for tokenRefresh event.
 */
const registerTokenRefreshCallback = () => {
    console.info('ATBase: DeviceRegistry registerTokenRefreshCallback')
    cordovaExecWithCallback(onTokenRefresh, null, DeviceRegistryConstants.NOTIFICATION_PLUGIN, DeviceRegistryConstants.ACTION_TOKEN_REFRESH, [])
        .catch((error) => {
            console.error('ATBase: DeviceRegistry error occurred while registering tokenRefreshCallback: ', JSON.stringify(error))
        })
}

/**
 * It is a callback function registered in notification plugin.
 * On tokenRefresh event in plugin, this functions is called back from plugin.
 */
const onTokenRefresh = () => {
    console.info('ATBase: DeviceRegistry onTokenRefresh called from plugin.')
    onTokenRefreshUpdateDevice()
}

/**
 * The method is called from notification plugin when pushToken is refreshed.
 * It internally calls updateDevice to send refreshed token to backend.
 * Collects appInstallId, pushToken, userAlias before making updateDevice call.
 */
export const onTokenRefreshUpdateDevice = async () => {
    console.info('ATBase: DeviceRegistry onTokenRefreshUpdateDevice')
    const deferred = generateDeferredPromise()
    if (EnvironmentDetail.isAndroid()) {
        try {
            const appInstallId = await getAppInstallId()
            const pushToken = await getPushToken()
            const userAlias = await getUserAlias()
            await updateDeviceOnTokenRefresh(appInstallId, pushToken, userAlias)
        } catch (error) {
            console.error('ATBase: DeviceRegistry: device registry update failed. ', JSON.stringify(error))
        } finally {
            deferred.resolve({})
        }
    } else {
        console.debug('ATBase: Not an android device. Logging out.')
        deferred.resolve({})
    }
    return deferred.promise
}

/**
 * Called from LogoutController. LogoutController waits on the returned promise then clears ihmLogin token.
 * Collects appInstallId, pushToken, userAlias before making updateDevice call.
 * @returns {Promise}: Resolves when:
 *                     1. _updateDevice call is successful.
 *                     2. _updateDevice call is failed.
 *                     2. _updateDevice call is not required.
 * Note: Promise need to be resolved in all cases.
 */
export const onLogoutDeregisterDevice = async () => {
    console.info('ATBase: DeviceRegistry onLogoutDeregisterDevice')
    const deferred = generateDeferredPromise()
    if (EnvironmentDetail.isAndroid()) {
        try {
            const appInstallId = await getAppInstallId()
            const pushToken = await getPushToken()
            const userAlias = await getUserAlias()
            console.info('ATBase: DeviceRegistry userAlias: ',userAlias)
            updateDeviceOnLogout(appInstallId, pushToken, userAlias)
        } catch (error) {
            console.error('ATBase DeviceRegistry onLogoutDeregisterDevice update failed: ', JSON.stringify(error))
        } finally {
            deferred.resolve({})
        }
    } else {
        console.debug('ATBase: Not an android device. Logging out.')
        deferred.resolve({})
    }

    return deferred.promise
}

/**
 * Fetches userAlias from UserInfoService.
 */
const getUserAlias = async (): Promise<any> => {
    return getUserInformation()
        .then((response) => {
            console.info('ATBase: DeviceRegistry: getUserInformation response from UserInfoService: ', JSON.stringify(response))
            if (response || response.userAlias) {
                return Promise.resolve(response.userAlias ? response.userAlias : response)
            }
            return Promise.reject('userAlias not present.')
        })
        .catch((error) => {
            console.error('ATBase: DeviceRegistry: unable to fetch userInfo. ', JSON.stringify(error))
            return Promise.reject(error instanceof Error ? error : new Error('SMP: DeviceRegistry: unable to fetch userInfo'))
        })
}

/**
 * Makes updateDevice request with LOGOUT flag to remove mapping from backend.
 */
const updateDeviceOnLogout = (appInstallId: string, pushToken: string, userAlias: string) => {
    console.info('ATBase deviceRegistryService updateDeviceOnLogout')
    return DeviceRegistryServiceClient.getInstance().updateDevice(createUpdateRequest(DeviceRegistryConstants.LOGOUT_EVENT, appInstallId, pushToken, userAlias))
}

/**
 * Makes registerDevice request if appInstallId is not present.
 * Makes updateDevice request if appInstallId is present.
 * Note: appInstallId is cached in local variable, before this call is made.
 */
const registerDevice = async (appInstallId: string, pushToken: string, userAlias: string) => {
    console.info('ATBase deviceRegistryService registerDevice')
    if (appInstallId) {
        console.info('ATBase deviceRegistryService updateDevice appInstallId: ',appInstallId)
        return DeviceRegistryServiceClient.getInstance().updateDevice(createUpdateRequest(DeviceRegistryConstants.LOGIN_EVENT, appInstallId, pushToken, userAlias))
    } else {
        return DeviceRegistryServiceClient.getInstance().registerDevice(createRegisterRequest(pushToken, userAlias))
    }
}

/**
 * Makes updateDevice request to update pushToken in backend with LOGIN flag.
 */
const updateDeviceOnTokenRefresh = (appInstallId: string, pushToken: string, userAlias: string) => {
    console.info('ATBase deviceRegistryService updateDeviceOnTokenRefresh')
    return DeviceRegistryServiceClient.getInstance().updateDevice(createUpdateRequest(DeviceRegistryConstants.LOGIN_EVENT, appInstallId, pushToken, userAlias))
}

/**
 * Creates updateRequest object.
 * @param authenticationEvent: Login/Logout event.
 */
const createUpdateRequest = (authenticationEvent: string, appInstallId: string, pushToken: string, userAlias: string) => {
    const storeId = EnvironmentDetail.getStoreId()
    const updateRequest = {
        appInstallId,
        authenticationEvent,
        applicationInformation: {},
        pushInformation: {
            provider: 'FCM',
            providerToken: pushToken,
        },
        userUpdateInformation: {
            userId: userAlias,
            storeId,
        },
    }
    return updateRequest
}

/**
 * Creates registerRequest object.
 */
const createRegisterRequest = (pushToken: string, userAlias: string) => {
    const registerRequest = {
        applicationInformation: {},
        pushInformation: {
            provider: 'FCM',
            providerToken: pushToken,
        }, userInformation: {
            userId: userAlias,
            storeId: EnvironmentDetail.getStoreId(),
        },
    }
    return registerRequest
}
